/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useEffect, useCallback, useState } from 'react';
import { UaParserDom } from '@userzoomplatform/ua-parser';
import { gql, useLazyQuery } from '@apollo/client';
import { useWebExtension } from '../../../providers/web-extension';
import {
	checkTimedOut,
	extensionUpdated,
	extensionUninstalled,
	extensionChecked,
	setRequirements,
} from '../../../providers/web-extension/actions';
import { ExtensionStatus, ExtensionEvents, ExtensionCheckTimeOut, Browsers } from '../../../utils/constants';

const { Chrome, Edge } = Browsers;

const extensionQuery = gql`
	query webExtension($browser: String, $beta: Boolean) {
		webExtension {
			installation(browser: $browser, beta: $beta) {
				minVersion
				id
			}
		}
	}
`;

let timeout: number;

type ExtensionData = {
	id: string;
};

const Extension = (): ReactElement => {
	const { state, dispatch } = useWebExtension();
	const [versionCheckStatus, setVersionCheckStatus] = useState<'checked' | 'unchecked'>('unchecked');
	const [extensionData, setExtensionData] = useState<ExtensionData>();

	const [device] = useState(() => {
		const uaParser = new UaParserDom(window.navigator.userAgent);
		return uaParser.getDevice();
	});

	const [getExtension, { called, loading, error, data }] = useLazyQuery(extensionQuery);

	const installationConfig = data?.webExtension?.installation;

	useEffect(
		function setRequirementsResponse() {
			if (!loading && !error && installationConfig) {
				dispatch(setRequirements(installationConfig.minVersion));
			}
		},
		[loading, error, installationConfig, dispatch]
	);

	const ensureCorrectVersionInstalled = useCallback(() => {
		if (!state.useDev && (device.browser?.name === Chrome || device.browser?.name === Edge) && !!installationConfig.id) {
			const incorrectVersion = !extensionData?.id || extensionData?.id !== installationConfig.id;
			if (incorrectVersion) {
				window.dispatchEvent(new Event(ExtensionEvents.UninstallExtension));
			}
		}
		setVersionCheckStatus('checked');
	}, [device, installationConfig, state.useDev, setVersionCheckStatus, extensionData]);

	const extensionVersionCb = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(event: any) => {
			const parsedEvent = { ...event, detail: JSON.parse(event.detail) };
			setExtensionData(parsedEvent.detail);
			clearTimeout(timeout);
			dispatch(extensionChecked(parsedEvent));
		},
		[dispatch, setExtensionData]
	);

	useEffect(() => {
		if (installationConfig && extensionData && versionCheckStatus === 'unchecked') {
			ensureCorrectVersionInstalled();
		}
	}, [installationConfig, extensionData, versionCheckStatus, ensureCorrectVersionInstalled, data]);

	const extensionUpdatedCb = useCallback(() => {
		dispatch(extensionUpdated());
	}, [dispatch]);

	const extensionUninstallingCb = useCallback(() => {
		dispatch(extensionUninstalled());
	}, [dispatch]);

	useEffect(() => {
		if (!called) {
			getExtension({ variables: { browser: device.browser?.name, beta: state.useBeta } });
		}
	}, [called, device, state.useBeta, getExtension]);

	useEffect(() => {
		timeout = setTimeout((isChecked: boolean) => {
			dispatch(checkTimedOut());
		}, ExtensionCheckTimeOut);
	}, [dispatch]);

	useEffect(() => {
		window.addEventListener(ExtensionEvents.ExtensionVersionResponse, extensionVersionCb);
		window.addEventListener(ExtensionEvents.ExtensionUpdated, extensionUpdatedCb);
		window.addEventListener(ExtensionEvents.ExtensionUninstalled, extensionUninstallingCb);

		window.dispatchEvent(new Event(ExtensionEvents.CheckExtensionVersion));

		return () => {
			window.removeEventListener(ExtensionEvents.ExtensionVersionResponse, extensionVersionCb);
			window.removeEventListener(ExtensionEvents.ExtensionUpdated, extensionUpdatedCb);
			window.removeEventListener(ExtensionEvents.ExtensionUninstalled, extensionUninstallingCb);
		};
	}, [dispatch, extensionUninstallingCb, extensionUpdatedCb, extensionVersionCb]);

	useEffect(() => {
		if (state.status === ExtensionStatus.Outdated) {
			window.dispatchEvent(new Event(ExtensionEvents.UpdateExtension));
		}
	}, [state.status]);

	return <div />;
};

export default Extension;
